// VRG-679 — small pure helpers for the Outreach pages.
import { OUTREACH_TARGET_STATUS_LABELS, type OutreachTargetStatus } from '@repo/types'
import { EMPTY_VALUE } from '@repo/ui'

const fmt = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

/** "Oct 14 to Oct 17, 2026"-style range; one side alone reads as "From …" / "Until …". */
export const formatCampaignDates = (start: string | null, end: string | null): string => {
  if (start && end) return start === end ? fmt(start) : `${fmt(start).replace(/, \d{4}$/, '')} to ${fmt(end)}`
  if (start) return `From ${fmt(start)}`
  if (end) return `Until ${fmt(end)}`
  return EMPTY_VALUE
}

/** A stored meeting is a day at noon UTC (TargetModal); show it as that day. */
export const formatMeetingDay = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : EMPTY_VALUE

/** VRG-684 — "$2,400 trip" in the currency it was paid in; `null` when no cost is recorded. */
export const formatTripCost = (cost: { amount: number; currency: string } | null): string | null => {
  if (!cost) return null
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cost.currency, maximumFractionDigits: 2 }).format(cost.amount)
  } catch {
    return `${cost.amount.toLocaleString('en-US')} ${cost.currency}`
  }
}

/** "Mark meeting held", "Mark LOI signed": lower case mid-sentence, acronyms kept. */
export const statusInSentence = (s: OutreachTargetStatus): string =>
  OUTREACH_TARGET_STATUS_LABELS[s]
    .split(' ')
    .map((w) => (/^[A-Z]{2,}$/.test(w) ? w : w.toLowerCase()))
    .join(' ')
